<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '../../stores/auth.ts';
import Avatar from './Avatar.vue';

const props = defineProps({
  showDashboard: {
    type: Boolean,
    default: false
  }
});

const router = useRouter();
const authStore = useAuthStore();
const isOpen = ref(false);

// Subscription data
const currentSubscription = ref(null);
const subscriptionLoading = ref(false);
const planLimits = ref(null);
const planLimitsLoading = ref(false);

const user = computed(() => authStore.user);
const userName = computed(() => user.value?.name || user.value?.email?.split('@')[0] || 'User');
const userEmail = computed(() => user.value?.email || '');
const userInitials = computed(() => {
  if (!userName.value) return 'U';
  return userName.value
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);
});

const userRole = computed(() => {
  return authStore.user?.company?.role || null;
});

const isEmployee = computed(() => {
  return userRole.value === 'employee';
});

const canManageCompany = computed(() => {
  return ['owner', 'admin'].includes(userRole.value || '');
});

const currentPlan = computed(() => {
  if (subscriptionLoading.value || planLimitsLoading.value) return 'Loading...';
  if (!currentSubscription.value?.plan && !planLimits.value?.plan_name) return 'Free Plan';
  const planName = planLimits.value?.plan_name || currentSubscription.value?.plan?.name;
  return planName ? `${planName} Plan` : 'Free Plan';
});

const planUsage = computed(() => {
  // Prefer plan limits data as it includes both invitations and hires
  if (planLimits.value && planLimits.value.max_monthly_invitations) {
    // Handle unlimited plans
    if (planLimits.value.max_monthly_invitations === 'Unlimited') {
      return {
        current: planLimits.value.current_month_total || 0,
        max: 'Unlimited'
      };
    }

    return {
      current: planLimits.value.current_month_total || 0,
      max: planLimits.value.max_monthly_invitations
    };
  }

  // Fallback to subscription data for backwards compatibility
  if (currentSubscription.value?.plan) {
    return {
      current: currentSubscription.value.current_month_hires || 0,
      max: currentSubscription.value.plan.max_hires
    };
  }

  return null;
});

const fetchCurrentSubscription = async () => {
  if (!authStore.isLoggedIn || isEmployee.value) {
    return;
  }

  subscriptionLoading.value = true;

  try {
    const response = await axios.get('/api/subscriptions/current');
    currentSubscription.value = response.data;
  } catch (error) {
    console.error('Error fetching subscription:', error);
  } finally {
    subscriptionLoading.value = false;
  }
};

const fetchPlanLimits = async () => {
  if (!authStore.isLoggedIn || isEmployee.value) {
    return;
  }

  planLimitsLoading.value = true;

  try {
    const response = await axios.get('/api/employee_invitations/plan_limits');
    planLimits.value = response.data;
  } catch (error) {
    console.error('Error fetching plan limits:', error);
    // Don't show error, just use fallback data
  } finally {
    planLimitsLoading.value = false;
  }
};

const closeDropdown = () => {
  isOpen.value = false;
};

const handleLogout = async () => {
  try {
    // Call auth store's logout method
    await authStore.logout();
    closeDropdown();

    // Navigate to sign-out success page
    window.location.href = '/sign-out-success';
  } catch (error) {
    console.error('Logout error:', error);
  }
};

// Close dropdown when clicking outside
const clickOutside = (event) => {
  const el = document.querySelector('.user-dropdown-container');
  if (el && !el.contains(event.target)) {
    closeDropdown();
  }
};

// Handle subscription refresh events
const handleSubscriptionRefresh = () => {
  if (authStore.isLoggedIn && canManageCompany.value) {
    fetchCurrentSubscription();
    fetchPlanLimits();
  }
};

onMounted(() => {
  document.addEventListener('click', clickOutside);
  window.addEventListener('subscription-updated', handleSubscriptionRefresh);
  window.addEventListener('invitation-sent', fetchPlanLimits);
  if (authStore.isLoggedIn && canManageCompany.value) {
    fetchCurrentSubscription();
    fetchPlanLimits();
  }
});

// Watch for auth changes
watch(() => authStore.user, () => {
  if (authStore.isLoggedIn && canManageCompany.value) {
    fetchCurrentSubscription();
    fetchPlanLimits();
  }
}, { immediate: true });

onUnmounted(() => {
  document.removeEventListener('click', clickOutside);
  window.removeEventListener('subscription-updated', handleSubscriptionRefresh);
  window.removeEventListener('invitation-sent', fetchPlanLimits);
});
</script>

<template>
  <div class="user-dropdown-container relative">
    <button
      @click="isOpen = !isOpen"
      class="user-dropdown-trigger flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors duration-200 focus:outline-none"
      :class="{ 'bg-gray-100': isOpen }"
    >
      <Avatar :user="user" size="sm" />
      <div class="flex flex-col items-start mobile--hidden">
        <span class="font-semibold text-gray-900 text-sm">{{ userName || 'User' }}</span>
        <div class="flex flex-col items-start text-xs text-gray-500">
          <span>{{ currentPlan }}</span>
          <span v-if="planUsage" class="text-xs text-gray-400">{{ planUsage.current }}/{{ planUsage.max }} this month</span>
        </div>
      </div>
      <i
        class="fas fa-chevron-down text-xs text-gray-500 ml-2 transition-transform duration-200"
        :class="{ 'transform rotate-180': isOpen }"
      ></i>
    </button>

    <transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <div
        v-show="isOpen"
        class="user-dropdown absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50"
        role="menu"
        tabindex="-1"
      >
        <div class="py-1" role="none">
          <div class="px-4 py-2 text-sm text-gray-700 border-b border-gray-100">
            <p class="font-medium">{{ userName || 'User' }}</p>
            <p class="text-xs text-gray-500 truncate">{{ userEmail }}</p>
          </div>

          <router-link
            v-if="showDashboard"
            to="/dashboard"
            class="dropdown-item"
            @click.native="closeDropdown"
          >
            <i class="fas fa-tachometer-alt"></i> Dashboard
          </router-link>

          <router-link
            to="/account/overview"
            class="dropdown-item"
            @click.native="closeDropdown"
          >
            <i class="fas fa-user-cog"></i> Account
          </router-link>

          <router-link
            v-if="canManageCompany"
            to="/account/subscription"
            class="dropdown-item"
            @click.native="closeDropdown"
          >
            <i class="fas fa-credit-card"></i> Subscription
          </router-link>

          <router-link
            v-if="canManageCompany"
            to="/company"
            class="dropdown-item"
            @click.native="closeDropdown"
          >
            <i class="fas fa-building"></i> Company
          </router-link>

          <div class="border-t border-gray-100 my-1"></div>

          <a
            href="mailto:support@integrateme.co.nz"
            class="dropdown-item"
            @click="closeDropdown"
          >
            <i class="fas fa-life-ring"></i> Support
          </a>

          <div class="border-t border-gray-100 my-1"></div>

          <button
            @click="handleLogout"
            class="dropdown-item text-red-600 hover--bg-red-50"
          >
            <i class="fas fa-sign-out-alt"></i> Sign out
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<style scoped>
.user-dropdown-container {
  position: relative;
  display: inline-block;
  height: 100%;
}

.user-dropdown-trigger {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  transition: all 150ms ease;
  outline: none;
  border: 1px solid transparent;
  background: transparent;
  cursor: pointer;
  height: 100%;
}

.user-dropdown-trigger:hover {
  background-color: rgba(243, 244, 246, 0.5);
}

.user-dropdown-trigger:focus {
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5);
  border-color: rgba(59, 130, 246, 0.5);
}

.user-dropdown-trigger.bg-gray-100 {
  background-color: #f3f4f6;
}

.user-avatar {
  width: 2rem;
  height: 2rem;
  border-radius: 9999px;
  background-color: #e0f2fe;
  color: #0369a1;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  flex-shrink: 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.user-dropdown {
  transform-origin: top right;
  position: absolute;
  right: 0;
  margin-top: 0.5rem;
  width: 16rem;
  border-radius: 0.5rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  background-color: white;
  border: 1px solid #e5e7eb;
  outline: none;
  z-index: 50;
  overflow: hidden;
}

.dropdown-header {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #f3f4f6;
  background-color: #f9fafb;
}

.dropdown-header .email {
  font-size: 0.75rem;
  color: #6b7280;
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dropdown-items {
  padding: 0.25rem 0;
}

.dropdown-item {
  display: flex;
  align-items: center;
  padding: 0.625rem 1rem;
  font-size: 0.875rem;
  color: #1f2937;
  width: 100%;
  text-align: left;
  transition: all 150ms ease;
  border: none;
  background: none;
  cursor: pointer;
}

.dropdown-item:hover {
  background-color: #f3f4f6;
  color: #111827;
}

.dropdown-item i {
  width: 1.25rem;
  text-align: center;
  margin-right: 0.75rem;
  color: #6b7280;
  font-size: 0.875rem;
}

.dropdown-divider {
  height: 1px;
  background-color: #f3f4f6;
  margin: 0.25rem 0;
}

.logout-button {
  color: #dc2626;
}

.logout-button:hover {
  background-color: #fef2f2;
  color: #b91c1c;
}

.logout-button i {
  color: #dc2626;
}

/* Animation for dropdown */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity 100ms ease, transform 100ms ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* Plan info styling */
.user-dropdown-trigger .flex.flex-col {
  justify-content: center;
}

@media (max-width: 768px) {
  .mobile--hidden {
    display: none;
  }

  .user-dropdown-trigger {
    gap: 0;
    padding: 0;
  }
}
</style>
